import { apiService } from "../../../shared/services/api/api.service";

export interface ReferralStats {
    totalRecruits: number;
    activeRecruits: number;
    inactiveRecruits: number;
    retentionRate: number;
    avgRecruitsPerRecruiter: number;
}

export interface TopRecruiter {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    totalRecruits: number;
    activeRecruits: number;
    retentionRate: number;
}

export interface ReferralChainNode {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    isActive: boolean;
    createdAt: string;
    recruits: ReferralChainNode[];
}

// Funnel types
export interface FunnelStepData {
    step: number;
    name: string;
    count: number;
    percentage: number;
    color: string;
}

export interface FunnelStatsResponse {
    funnel: FunnelStepData[];
    conversionRates: {
        step1ToStep2: number;
        step2ToStep3: number;
        step3ToStep4: number;
        step4ToStep5: number;
        step5ToStep6: number;
        step6ToStep7: number;
        step7ToStep8: number;
    };
    totalInvites: number;
}

export interface FunnelAgent {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    currentStep: number;
    stepName: string;
    invitedAt: string | null;
    state: string | null;
    campaignName: string | null;
}

export interface FunnelAgentsResponse {
    agents: FunnelAgent[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

export interface FunnelFilters {
    state?: string;
    campaignId?: string;
    dateFrom?: string;
    dateTo?: string;
    step?: number;
    page?: number;
    limit?: number;
}

class ReferralAnalyticsService {
    /**
     * Get overall referral statistics
     */
    async getStats(): Promise<ReferralStats> {
        const response = await apiService.get<{ success: boolean; data: ReferralStats }>(
            "/referral-analytics/stats"
        );
        return response.data.data;
    }

    /**
     * Get top recruiters by effectiveness
     */
    async getTopRecruiters(limit: number = 10): Promise<TopRecruiter[]> {
        const response = await apiService.get<{ success: boolean; data: TopRecruiter[] }>(
            `/referral-analytics/top-recruiters?limit=${limit}`
        );
        return response.data.data;
    }

    /**
     * Get referral chain for a specific user
     */
    async getReferralChain(userId: string): Promise<ReferralChainNode | null> {
        const response = await apiService.get<{ success: boolean; data: ReferralChainNode | null }>(
            `/referral-analytics/chain/${userId}`
        );
        return response.data.data;
    }

    /**
     * Get complete referral hierarchy
     */
    async getHierarchy(): Promise<ReferralChainNode[]> {
        const response = await apiService.get<{ success: boolean; data: ReferralChainNode[] }>(
            "/referral-analytics/hierarchy"
        );
        return response.data.data;
    }

    /**
     * Get funnel statistics with filters
     */
    async getFunnelStats(filters: Omit<FunnelFilters, 'step' | 'page' | 'limit'>): Promise<FunnelStatsResponse> {
        const params = new URLSearchParams();
        if (filters.state) params.append('state', filters.state);
        if (filters.campaignId) params.append('campaignId', filters.campaignId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);

        const queryString = params.toString();
        const response = await apiService.get<{ success: boolean; data: FunnelStatsResponse }>(
            `/referral-analytics/funnel-stats${queryString ? `?${queryString}` : ''}`
        );
        return response.data.data;
    }

    /**
     * Get agents at a specific funnel step
     */
    async getFunnelAgents(filters: FunnelFilters): Promise<FunnelAgentsResponse> {
        const params = new URLSearchParams();
        if (filters.state) params.append('state', filters.state);
        if (filters.campaignId) params.append('campaignId', filters.campaignId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.step) params.append('step', filters.step.toString());
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());

        const queryString = params.toString();
        const response = await apiService.get<{ success: boolean; data: FunnelAgentsResponse }>(
            `/referral-analytics/funnel-agents${queryString ? `?${queryString}` : ''}`
        );
        return response.data.data;
    }

    /**
     * Send onboarding reminder emails
     */
    async sendReminder(agentIds: string[]): Promise<{ success: boolean; sentCount: number }> {
        const response = await apiService.post<{ success: boolean; data: { success: boolean; sentCount: number } }>(
            '/referral-analytics/send-reminder',
            { agentIds }
        );
        return response.data.data;
    }
}

const referralAnalyticsService = new ReferralAnalyticsService();
export default referralAnalyticsService;
